/**
 * Bullet art. Enemy fire uses a hot palette with white cores and dark rims so
 * it reads on every biome; friendly fire stays cool/weapon-tinted. Sprites
 * are cached per kind and radius and rotated along the velocity.
 */
import type { Bullet } from "../types";
import { weaponInfo } from "../content";
import { INK, TAU, disc, glow, line, linear, poly, radial, rgba, shade, stamp } from "./kit";

type Ctx = CanvasRenderingContext2D;
/** Enemy shots render a little larger than their hit radius (never smaller). */
const ENEMY_ART = 1.3;
const ENEMY: Record<string, string> = {
  pellet: "#FF2E63",
  enemy: "#FF2E63",
  boss: "#FF2E63",
  needle: "#FF9F1C",
  ring: "#B45CFF",
  orb: "#FF3DA8",
  blade: "#FF4757",
  mine: "#FF5E3A",
  missile: "#A45CFF",
  bounce: "#FF7A1A",
};
function halo(c: Ctx, r: number, color: string, sx = 1, sy = 1) {
  c.save();
  c.scale(sx, sy);
  c.fillStyle = radial(c, 0, 0, 0, r, [
    [0, rgba(color, 0.55)],
    [0.5, rgba(color, 0.2)],
    [1, rgba(color, 0)],
  ]);
  c.fillRect(-r, -r, r * 2, r * 2);
  c.restore();
}
function hot(c: Ctx, r: number, color: string) {
  return radial(c, 0, 0, 0, r, [
    [0, "#FFFFFF"],
    [0.42, "#FFFFFF"],
    [0.62, shade(color, 0.35)],
    [1, color],
  ]);
}

/** Static enemy bullet sprite, facing up (-y) along its velocity. */
function enemySprite(c: Ctx, kind: string, r: number) {
  const color = ENEMY[kind] ?? ENEMY.pellet;
  switch (kind) {
    case "needle":
      halo(c, r * 3.2, color, 0.55, 1);
      poly(c, [[0, -r * 2.4], [r * 0.95, 0], [0, r * 1.7], [-r * 0.95, 0]], linear(c, -r, 0, r, 0, [[0, color], [0.5, "#FFF6D8"], [1, color]]), INK, 1.4);
      break;
    case "ring":
      halo(c, r * 2, color);
      c.beginPath();
      c.arc(0, 0, r * 0.82, 0, TAU);
      c.strokeStyle = INK;
      c.lineWidth = r * 0.52;
      c.stroke();
      c.strokeStyle = color;
      c.lineWidth = r * 0.38;
      c.stroke();
      c.strokeStyle = "#FFFFFF";
      c.lineWidth = r * 0.14;
      c.stroke();
      break;
    case "orb":
      halo(c, r * 2.2, color);
      disc(c, 0, 0, r * 1.02, INK);
      disc(c, 0, 0, r * 0.92, radial(c, 0, 0, 0, r, [[0, "#FFFFFF"], [0.3, "#FFD2F0"], [0.7, color], [1, "#7A1FD6"]]));
      c.beginPath();
      c.arc(0, 0, r * 0.62, 0.3, 2.2);
      c.strokeStyle = "rgba(255,255,255,0.8)";
      c.lineWidth = r * 0.12;
      c.stroke();
      break;
    case "blade":
      halo(c, r * 2, color);
      poly(
        c,
        Array.from({ length: 8 }, (_, i) => {
          const a = (i / 8) * TAU,
            k = i % 2 ? r * 0.45 : r * 1.55;
          return [Math.cos(a) * k, Math.sin(a) * k];
        }),
        linear(c, -r, -r, r, r, [[0, "#FFE1E4"], [0.5, color], [1, "#9C1328"]]),
        INK,
        1.4,
      );
      disc(c, 0, 0, r * 0.3, "#FFFFFF");
      break;
    case "missile":
      halo(c, r * 2.2, color, 0.7, 1);
      poly(c, [[0, -r * 2], [r * 0.7, -r * 0.6], [r * 0.7, r * 1.0], [r * 1.15, r * 1.5], [-r * 1.15, r * 1.5], [-r * 0.7, r * 1.0], [-r * 0.7, -r * 0.6]], linear(c, -r, 0, r, 0, [[0, shade(color, 0.3)], [1, shade(color, -0.3)]]), INK, 1.4);
      poly(c, [[0, -r * 2], [r * 0.7, -r * 0.6], [-r * 0.7, -r * 0.6]], "#FFF2F8", INK, 1.2);
      break;
    case "bounce":
      halo(c, r * 2, color);
      poly(c, [[-r, -r * 0.6], [-r * 0.6, -r], [r * 0.6, -r], [r, -r * 0.6], [r, r * 0.6], [r * 0.6, r], [-r * 0.6, r], [-r, r * 0.6]], hot(c, r * 1.2, color), INK, 1.5);
      break;
    default:
      halo(c, r * 2.1, color);
      disc(c, 0, 0, r * 1.08, INK);
      disc(c, 0, 0, r * 0.94, hot(c, r, color));
  }
}
function mineSprite(c: Ctx, r: number, armed: boolean) {
  const color = armed ? "#FF3D3D" : "#8A93A6";
  if (armed) halo(c, r * 2, "#FF3D3D");
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU;
    poly(c, [[Math.cos(a - 0.2) * r * 0.7, Math.sin(a - 0.2) * r * 0.7], [Math.cos(a) * r * 1.25, Math.sin(a) * r * 1.25], [Math.cos(a + 0.2) * r * 0.7, Math.sin(a + 0.2) * r * 0.7]], armed ? "#FFB0A0" : "#C9CFDA", INK, 1.2);
  }
  disc(c, 0, 0, r * 0.82, linear(c, -r, -r, r, r, [[0, shade(color, 0.3)], [1, shade(color, -0.45)]]), INK, 1.6);
  disc(c, 0, 0, r * 0.32, armed ? "#FFFFFF" : "#FFD65C", INK, 1.2);
}

function beam(c: Ctx, b: Bullet, tick: number, color: string, length: number, core: number, fade: number) {
  c.save();
  c.globalAlpha = fade;
  const flicker = 1 + Math.sin(tick * 1.7 + b.id) * 0.08;
  c.lineCap = "round";
  for (const [w, stroke] of [
    [core * 3.4 * flicker, rgba(color, 0.22)],
    [core * 2.1 * flicker, rgba(color, 0.7)],
    [core * 1.1, shade(color, 0.55)],
    [core * 0.45, "#FFFFFF"],
  ] as const) {
    c.strokeStyle = stroke;
    c.lineWidth = w;
    c.beginPath();
    c.moveTo(0, 0);
    c.lineTo(0, length);
    c.stroke();
  }
  c.restore();
}

/**
 * Returns true when handled; false means the caller should draw the pilot's
 * own hand-drawn bullet sprite (straight / double / fan / wing).
 */
export function paintBullet(c: Ctx, b: Bullet, tick: number): boolean {
  const enemy = b.owner === "enemy",
    angle = Math.atan2(b.vy, b.vx) + Math.PI / 2,
    r = b.radius,
    age = b.age ?? 0;
  if (b.kind === "enemy-beam") {
    c.save();
    c.translate(b.x, b.y);
    c.rotate((b.turn ?? Math.PI / 2) - Math.PI / 2);
    if (b.warmup) {
      const pulse = 0.5 + 0.5 * Math.sin(tick * 0.6);
      c.strokeStyle = rgba("#FF3D5E", 0.35 + pulse * 0.4);
      c.lineWidth = 2;
      c.setLineDash([14, 10]);
      c.lineDashOffset = -tick * 2;
      c.beginPath();
      c.moveTo(0, 0);
      c.lineTo(0, 1500);
      c.stroke();
      c.setLineDash([]);
      c.strokeStyle = rgba("#FF3D5E", 0.12 + pulse * 0.1);
      c.lineWidth = r * 2;
      c.stroke();
      glow(c, 0, 0, r * (1.2 + (1 - Math.min(1, b.warmup / 80)) * 1.6), "#FF3D5E", 0.9);
    } else {
      beam(c, b, tick, "#FF2E7A", 1500, r * 1.6, 1);
      glow(c, 0, 0, r * 3, "#FF5CA0", 1);
      for (let i = 0; i < 6; i++) {
        const y = ((tick * 26 + i * 250) % 1500) + 0;
        disc(c, Math.sin(i + tick) * r * 0.4, y, r * 0.5, "rgba(255,255,255,0.8)");
      }
    }
    c.restore();
    return true;
  }
  if (enemy) {
    const color = ENEMY[b.kind] ?? ENEMY.pellet;
    if (b.kind === "mine") {
      const armed = !b.warmup;
      stamp(c, `mine:${armed ? 1 : 0}:${Math.round(r)}`, r * 4.2, r * 4.2, (g) => mineSprite(g, r, armed), b.x, b.y, age * 0.02, 1.15);
      if (!armed && Math.floor(tick / 8) % 2) glow(c, b.x, b.y, r * 0.9, "#FFD65C", 0.9);
      if (armed) {
        c.strokeStyle = rgba("#FF3D3D", 0.35 + 0.25 * Math.sin(tick * 0.4));
        c.lineWidth = 2;
        c.beginPath();
        c.arc(b.x, b.y, r * 2.1, 0, TAU);
        c.stroke();
      }
      return true;
    }
    if (b.kind === "missile") {
      for (let i = 1; i <= 4; i++) {
        const k = i * 5;
        disc(c, b.x - b.vx * k * 0.35, b.y - b.vy * k * 0.35, r * (0.55 - i * 0.08), `rgba(255,190,120,${0.5 - i * 0.1})`);
      }
      glow(c, b.x - b.vx * 1.6, b.y - b.vy * 1.6, r * 1.1, "#FF9A3D", 0.9);
    }
    const spin =
      b.kind === "blade" ? age * 0.32 : b.kind === "bounce" ? age * 0.06 : b.kind === "needle" || b.kind === "missile" ? angle : 0;
    const pulse = b.kind === "orb" ? 1 + Math.sin(age * 0.3) * 0.07 : 1;
    const size = r * (b.kind === "needle" ? 6.6 : 4.6);
    stamp(c, `eb:${b.kind}:${Math.round(r)}`, size, size, (g) => enemySprite(g, b.kind, r), b.x, b.y, spin, pulse * ENEMY_ART);
    if (b.kind === "orb") glow(c, b.x, b.y, r * 1.6, color, 0.5);
    return true;
  }
  // Friendly fire.
  const color = friendlyColor(b);
  switch (b.kind) {
    case "laser":
    case "rail": {
      c.save();
      c.translate(b.x, b.y);
      const rail = b.kind === "rail",
        fade = Math.min(1, b.life / (rail ? 6 : 4));
      beam(c, b, tick, color, -1400, r * (rail ? 1.1 : 1), fade);
      if (rail)
        for (let i = 0; i < 6; i++) {
          const y = -((age * 60 + i * 230) % 1400);
          c.strokeStyle = rgba("#FFFFFF", 0.7 * fade);
          c.lineWidth = 2.5;
          c.beginPath();
          c.ellipse(0, y, r * 1.9, r * 0.6, 0, 0, TAU);
          c.stroke();
        }
      glow(c, 0, 0, r * 3.2, color, fade);
      c.restore();
      return true;
    }
    case "flame": {
      const k = Math.min(1, age / 30),
        size = r * (0.8 + k * 1.1),
        stage = Math.min(3, Math.floor(age / 8));
      c.globalAlpha = 1 - k * 0.55;
      stamp(c, `flame:${stage}`, 64, 64, (g) => {
        const tone = ["#FFF6C8", "#FFD45C", "#FF9A3D", "#F2542D"][stage];
        g.fillStyle = radial(g, 0, 0, 0, 32, [
          [0, "#FFFFFF"],
          [0.3, tone],
          [0.75, rgba(tone, 0.55)],
          [1, rgba(tone, 0)],
        ]);
        g.fillRect(-32, -32, 64, 64);
      }, b.x + Math.sin(b.id + age * 0.4) * 3, b.y, 0, size / 22);
      c.globalAlpha = 1;
      return true;
    }
    case "rocket": {
      for (let i = 1; i <= 6; i++) {
        const back = i * 7;
        disc(c, b.x - b.vx * back * 0.18 + Math.sin(b.id + i) * 2, b.y - b.vy * back * 0.18, 3 + i * 1.3, `rgba(226,230,236,${0.55 - i * 0.08})`);
      }
      glow(c, b.x - b.vx * 2.2, b.y - b.vy * 2.2, 13, "#FF9A3D", 1);
      stamp(c, "rocket:" + color, 26, 40, (g) => {
        poly(g, [[0, -17], [6, -8], [6, 9], [11, 15], [-11, 15], [-6, 9], [-6, -8]], linear(g, -6, 0, 6, 0, [[0, "#FFFFFF"], [1, "#B9C3D1"]]), INK, 1.8);
        poly(g, [[0, -17], [6, -8], [-6, -8]], color, INK, 1.5);
        poly(g, [[-11, 15], [-6, 9], [-6, 15]], color, null);
        poly(g, [[11, 15], [6, 9], [6, 15]], color, null);
      }, b.x, b.y, angle);
      return true;
    }
    case "homing":
      glow(c, b.x - b.vx * 1.5, b.y - b.vy * 1.5, 9, "#7FF3D0", 0.9);
      stamp(c, "homing", 18, 30, (g) => {
        poly(g, [[0, -12], [5, -2], [4, 10], [-4, 10], [-5, -2]], linear(g, -5, 0, 5, 0, [[0, "#D9FFF3"], [1, "#2FB28C"]]), INK, 1.5);
      }, b.x, b.y, angle);
      return true;
    case "boomerang":
      stamp(c, "boomerang", 44, 44, (g) => {
        g.beginPath();
        g.arc(0, 0, 17, Math.PI * 0.15, Math.PI * 1.35);
        g.arc(-4, -3, 11, Math.PI * 1.35, Math.PI * 0.15, true);
        g.closePath();
        g.fillStyle = linear(g, -17, -17, 17, 17, [[0, "#FFF1B0"], [1, "#E0A02A"]]);
        g.fill();
        g.strokeStyle = INK;
        g.lineWidth = 2;
        g.stroke();
      }, b.x, b.y, age * 0.35);
      return true;
    case "split":
    case "fragment": {
      const big = b.kind === "split";
      glow(c, b.x, b.y, r * (big ? 2.2 : 1.4), "#7FE9FF", 0.8);
      stamp(c, "split:" + (big ? 1 : 0) + ":" + Math.round(r), r * 3, r * 3, (g) => {
        disc(g, 0, 0, r * 0.9, INK);
        disc(g, 0, 0, r * 0.78, radial(g, 0, 0, 0, r, [[0, "#FFFFFF"], [0.5, "#9FF4FF"], [1, "#2F9FD0"]]));
        if (big) for (let i = 0; i < 4; i++) line(g, [[Math.cos(i * 1.57) * r * 0.3, Math.sin(i * 1.57) * r * 0.3], [Math.cos(i * 1.57) * r * 0.75, Math.sin(i * 1.57) * r * 0.75]], "rgba(20,60,90,0.6)", 1.5);
      }, b.x, b.y, age * 0.1);
      return true;
    }
    case "orbit":
      c.strokeStyle = rgba("#9FF4FF", 0.6);
      c.lineWidth = 2;
      c.beginPath();
      c.arc(b.x, b.y, r, 0, TAU);
      c.stroke();
      return true;
  }
  return false;
}

export function friendlyColor(b: Bullet) {
  try {
    return weaponInfo(b.kind as any)?.color ?? "#6FD6FF";
  } catch {
    return "#6FD6FF";
  }
}
/** Short luminous streak behind hand-drawn bullets, tinted by weapon. */
export function bulletTrail(c: Ctx, b: Bullet) {
  const speed = Math.hypot(b.vx, b.vy);
  if (speed < 1) return;
  const len = Math.min(46, speed * 1.8),
    nx = b.vx / speed,
    ny = b.vy / speed,
    color = b.kind === "wing" ? "#9FF4FF" : shade(friendlyColor(b), 0.35);
  c.save();
  c.lineCap = "round";
  c.strokeStyle = linear(c, b.x, b.y, b.x - nx * len, b.y - ny * len, [
    [0, rgba(color, 0.75)],
    [1, rgba(color, 0)],
  ]);
  c.lineWidth = b.radius * 1.3;
  c.beginPath();
  c.moveTo(b.x, b.y);
  c.lineTo(b.x - nx * len, b.y - ny * len);
  c.stroke();
  c.restore();
}
